import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import QuickAddMenu from '../../components/ui/QuickAddMenu';
import Icon from '../../components/AppIcon';
import SEO from '../../components/SEO';
import PermissionDenied from '../../components/ui/PermissionDenied';
import SubscriptionTab from './components/SubscriptionTab';
import TeamManagementTab from './components/TeamManagementTab';
import PermissionsTab from './components/PermissionsTab';
import OrganizationTab from './components/OrganizationTab';
import ProfileTab from './components/ProfileTab';
import NotificationsTab from './components/NotificationsTab';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';

const Settings = () => {
  const { user, organization, subscription, loading: authLoading, refreshOrganization } = useAuth();
  const permissions = usePermissions();
  const [activeTab, setActiveTab] = useState('profile');
  const [tabError, setTabError] = useState('');
  const navigate = useNavigate();
  const tabsContainerRef = useRef(null);
  const contentRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => {
    // Listen for navigation events from other screens
    const handleNavigateToSettings = (event) => {
      const { tab } = event?.detail || {};
      if (tab) {
        setActiveTab(tab);
      }
    };

    window.addEventListener('navigateToSettings', handleNavigateToSettings);

    return () => {
      window.removeEventListener('navigateToSettings', handleNavigateToSettings);
    };
  }, []);

  const isAdmin = permissions?.role === 'admin';
  const isOperator = permissions?.role === 'operator';
  const isViewer = permissions?.role === 'viewer';

  // Tab visibility rules:
  // admin: all tabs
  // operator: organization tab only (profile update)
  // viewer: organization tab (read-only)
  const allTabs = [
    { id: 'profile', label: 'Profile', icon: 'User', allowedRoles: ['admin', 'operator', 'viewer'] },
    { id: 'organization', label: 'Organization', icon: 'Building2', allowedRoles: ['admin', 'operator', 'viewer'] },
    { id: 'notifications', label: 'Notifications', icon: 'Bell', allowedRoles: ['admin', 'operator', 'viewer'] },
    { id: 'subscription', label: 'Subscription', icon: 'CreditCard', allowedRoles: ['admin'] },
    { id: 'team', label: 'Team Management', icon: 'Users', allowedRoles: ['admin'] },
    { id: 'permissions', label: 'Permissions', icon: 'Shield', allowedRoles: ['admin'] },
  ];

  const visibleTabs = allTabs?.filter(tab => 
    permissions?.loading || tab?.allowedRoles?.includes(permissions?.role)
  );

  const handleTabClick = (tabId) => {
    const tab = allTabs?.find(t => t?.id === tabId);
    if (tab && !tab?.allowedRoles?.includes(permissions?.role)) {
      setTabError(`The "${tab?.label}" tab is restricted to admin users only.`);
      setTimeout(() => setTabError(''), 4000);
      return;
    }
    setTabError('');
    setActiveTab(tabId);
    // Scroll active tab into view on mobile
    setTimeout(() => {
      const activeTabEl = tabsContainerRef?.current?.querySelector('[data-active="true"]');
      activeTabEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, 50);
  };

  // Swipe gesture handler for tab navigation
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e?.touches?.[0]?.clientX;
    touchStartY.current = e?.touches?.[0]?.clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX?.current === null) return;
    const deltaX = e?.changedTouches?.[0]?.clientX - touchStartX?.current;
    const deltaY = e?.changedTouches?.[0]?.clientY - (touchStartY?.current || 0);
    // Only trigger if horizontal swipe is dominant and significant (>60px)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 60) {
      const currentIndex = visibleTabs?.findIndex(t => t?.id === activeTab);
      if (deltaX < 0 && currentIndex < visibleTabs?.length - 1) {
        // Swipe left → next tab
        handleTabClick(visibleTabs?.[currentIndex + 1]?.id);
      } else if (deltaX > 0 && currentIndex > 0) {
        // Swipe right → previous tab
        handleTabClick(visibleTabs?.[currentIndex - 1]?.id);
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
  }, [activeTab, visibleTabs]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="main-content">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading settings...</p>
            </div>
          </div>
        </main>
        <QuickAddMenu />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Account Settings — ADU Flow"
        description="Manage your ADU Flow account settings. Update your profile, organization details, team permissions, notification preferences, and subscription plan."
        canonical="/settings"
        keywords="account settings, profile, organization, team management, permissions, notifications, subscription"
        noIndex={true}
      />
      <Header />
      
      <main className="main-content">
        <div className="container mx-auto px-4 py-6 md:py-8">
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Settings</h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Manage your account, subscription, and organization settings
                </p>
              </div>
              {isViewer && (
                <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg text-sm text-muted-foreground">
                  <Icon name="Eye" size={16} />
                  <span>Read-only access</span>
                </div>
              )}
            </div>
          </div>

          {/* Permission Denied Banner */}
          {tabError && (
            <div className="mb-4">
              <PermissionDenied message={tabError} />
            </div>
          )}

          {/* Role-based info banners */}
          {isViewer && (
            <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-warning/10 border border-warning/20 rounded-lg text-sm text-warning">
              <Icon name="Info" size={18} className="flex-shrink-0" />
              <span>You have read-only access to settings. Team management, billing, and organization changes are restricted to admin users.</span>
            </div>
          )}
          {isOperator && (
            <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              <Icon name="Info" size={18} className="flex-shrink-0" />
              <span>As an operator, you can update organization profile settings. Team management and billing are restricted to admin users.</span>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-border mb-6">
            <div ref={tabsContainerRef} className="flex overflow-x-auto scrollbar-hide -mb-px">
              {visibleTabs?.map((tab) => (
                <button
                  key={tab?.id}
                  data-active={activeTab === tab?.id}
                  onClick={() => handleTabClick(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-4 min-h-[52px] text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-accent text-primary' : 'border-transparent text-muted-foreground hover:text-primary hover:border-primary/30'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content - swipe gesture support */}
          <div
            ref={contentRef}
            className="space-y-6"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {activeTab === 'profile' && (
              <ProfileTab />
            )}
            {activeTab === 'notifications' && (
              <NotificationsTab />
            )}
            {activeTab === 'subscription' && isAdmin && (
              <SubscriptionTab subscription={subscription} organization={organization} />
            )}
            {activeTab === 'team' && isAdmin && (
              <TeamManagementTab />
            )}
            {activeTab === 'permissions' && isAdmin && (
              <PermissionsTab />
            )}
            {activeTab === 'organization' && (
              <OrganizationTab organization={organization} readOnly={isViewer} refreshOrganization={refreshOrganization} />
            )}
            {/* Fallback for restricted tab access */}
            {((activeTab === 'subscription' || activeTab === 'team' || activeTab === 'permissions') && !isAdmin) && (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Icon name="Lock" size={28} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Access Restricted</h3>
                <p className="text-sm text-muted-foreground text-center max-w-sm">
                  This settings section is available to admin users only.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <QuickAddMenu />
    </div>
  );
};

export default Settings;